import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType, Overlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injectable, input, TemplateRef } from '@angular/core';
import { X } from 'lucide';
import { NbIcon } from '../../icon/icon';
import { injectNbUiText } from '../../i18n/ui-text';

export type DrawerSize = 'md' | 'lg';

/**
 * `NbDrawer` — opens content in a right-hand drawer (2026-09 styre prototype:
 * detail views for a sak, beboer, dokument, vedlikeholdslogg, undersøkelse).
 *
 * Built on CDK `Dialog`, so it comes with `role="dialog"` + `aria-modal`, a
 * focus trap, Escape-to-close, focus restore to the opener and an inert
 * background. Width: `md` = 480px, `lg` = 560px (sak / vedlikeholdslogg).
 *
 * Accessible name (WCAG 4.1.2): pass `ariaLabel` in the config — it lands on the
 * CDK dialog container. When omitted, an `<nb-drawer>` inside the content labels
 * the container with its own title (`aria-labelledby`).
 *
 * Deep-linkable detail ROUTES stay routes (S4): a drawer supplements a route,
 * it never replaces one — if it must, mirror its state in a query param.
 *
 * @example
 * const ref = this.drawer.open(SakDetaljDrawer, { data: { id }, size: 'lg', ariaLabel: 'Sak' });
 */
@Injectable({ providedIn: 'root' })
export class NbDrawer {
  private readonly cdk = inject(Dialog);
  private readonly overlay = inject(Overlay);

  open<R = unknown, D = unknown, C = unknown>(
    content: ComponentType<C> | TemplateRef<C>,
    config: DialogConfig<D, DialogRef<R, C>> & { size?: DrawerSize; ariaLabel?: string | null } = {},
  ): DialogRef<R, C> {
    const { size = 'md', ariaLabel = null, ...rest } = config;
    return this.cdk.open<R, D, C>(content, {
      backdropClass: 'nb-scrim',
      panelClass: size === 'lg' ? ['nb-drawer-panel', 'lg'] : 'nb-drawer-panel',
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().right('0').top('0'),
      ariaLabel,
      ...rest,
    });
  }
}

/**
 * `<nb-drawer>` — the drawer's visual shell: kicker (uppercase overline),
 * title, close button, scrolling body and an optional footer
 * (`[nbDrawerFooter]`). Use inside a component opened via `NbDrawer.open()`.
 * The title also names the CDK dialog container (`aria-labelledby`) unless the
 * opener passed an explicit `ariaLabel`. `closeLabel` localises the close button.
 *
 * @example
 * <nb-drawer kicker="Sak #12" title="Lekkasje i kjeller" [closeLabel]="'common.close' | translate">
 *   …
 *   <nb-button nbDrawerFooter variant="pri">Lagre</nb-button>
 * </nb-drawer>
 */
@Component({
  selector: 'nb-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  host: { '[attr.aria-labelledby]': 'titleId' },
  template: `<div class="nb-drawer">
    <header class="nb-drawer-head">
      <div class="nb-drawer-titles">
        @if (kicker()) {
          <span class="nb-drawer-kicker">{{ kicker() }}</span>
        }
        <h2 class="nb-drawer-title" [id]="titleId">{{ title() }}</h2>
      </div>
      <button type="button" class="nb-drawer-close" [attr.aria-label]="closeText()" (click)="close()">
        <nb-icon [icon]="X" [size]="20" />
      </button>
    </header>
    <div class="nb-drawer-body"><ng-content /></div>
    <footer class="nb-drawer-foot"><ng-content select="[nbDrawerFooter]" /></footer>
  </div>`,
  styleUrl: './drawer.css',
})
export class NbDrawerShell {
  private readonly ref = inject<DialogRef<unknown, unknown>>(DialogRef, { optional: true });

  readonly title = input.required<string>();
  /** Small uppercase line above the title ("Sak #12", "Beboer"). */
  readonly kicker = input<string>();
  /** Accessible name of the close button — pass a translated string from the app. */
  readonly closeLabel = input<string | undefined>(undefined);
  private readonly uiText = injectNbUiText();
  protected readonly closeText = computed(() => this.closeLabel() ?? this.uiText().close);

  protected readonly titleId = `nb-drawer-${nextId++}`;
  protected readonly X = X;

  constructor() {
    // Name the CDK dialog container by the drawer title (ignored by CDK when the
    // opener supplied `ariaLabel`, which then wins). Duck-typed: the container
    // API is CDK-internal and absent when the shell is rendered outside a dialog.
    const container = this.ref?.containerInstance as
      | { _addAriaLabelledBy?: (id: string) => void; _removeAriaLabelledBy?: (id: string) => void }
      | undefined;
    if (container?._addAriaLabelledBy) {
      container._addAriaLabelledBy(this.titleId);
      inject(DestroyRef).onDestroy(() => container._removeAriaLabelledBy?.(this.titleId));
    }
  }

  protected close(): void {
    this.ref?.close();
  }
}

let nextId = 0;
